import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { motion as motionTokens } from "@tokens/index";
import { fadeInThumb, placeThumb, px, springThumb, thumbTransform } from "./thumbMotion";
import { useReducedMotionPreference } from "./useReducedMotionPreference";

/**
 * SlidingPill — a single absolutely-positioned element that animates
 * to the bounding box of whichever sibling is "active."
 *
 * Theory grounding:
 *   - Aesthetic-theory §5 §5 (Choreography / Shared Element): "One element
 *     visually persists and transforms between two views — spatial
 *     continuity; object permanence; 'this is the same thing.'"
 *   - Aesthetic-theory §5 §6 (Transition Semantics / State Change): "Morph,
 *     crossfade, color shift — same object, new state; identity preserved."
 *   - Atlas 01 §2 (FLIP technique): capture old position, measure new,
 *     invert, animate.
 *
 * Unlike `layoutId`, this approach:
 *   1. Never unmounts the pill — it is always in the DOM, so there is
 *      no mount/unmount race, no `LayoutGroup` dependency, no
 *      `AnimatePresence` interference.
 *   2. Measures real DOM rects (via refs) so it works across variable-
 *      length lists, heterogeneous row heights, and scroll containers.
 *   3. Moves through `thumbMotion`: one composed `transform` plus
 *      `width`/`height`, all Web Animations driven by the `morph` spring
 *      (critically damped, no bounce, no layout flutter, no script per
 *      frame). Aesthetic-theory §2 §2 (Spring Dynamics): "High stiffness,
 *      high damping = quick, controlled, precise."
 *
 * Usage:
 *   <div style={{ position: "relative" }}>
 *     <SlidingPill
 *       containerRef={containerRef}
 *       activeIndex={focusIndex}
 *       itemSelector="[data-pill-target]"
 *       style={{ borderRadius: radius.md, background: ... }}
 *     />
 *     {items.map((item, i) => (
 *       <div key={item.id} data-pill-target ...>
 *         {item.label}
 *       </div>
 *     ))}
 *   </div>
 *
 * The pill measures its target by finding the nth element matching
 * `itemSelector` inside `containerRef`. It positions itself relative
 * to the container's bounding rect.
 *
 * Measurement runs in a layout effect, so the pill is in place on the
 * first painted frame. A second measurement one frame later catches
 * targets that were still moving (stagger, entrance transforms). The
 * first appearance fades in; later moves morph.
 */

export interface SlidingPillProps {
    /** Ref to the scroll/layout container the pill lives inside. */
    containerRef: React.RefObject<HTMLElement | null>;
    /** Index of the active item (0-based), or -1 / null to hide. */
    activeIndex: number | null;
    /** CSS selector used to find target elements inside the container. */
    itemSelector: string;
    /** Visual style of the pill surface (background, border, shadow, radius). */
    style?: CSSProperties;
    /** Extra className if needed. */
    className?: string;
}

interface PillRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Layout geometry first: `offset*` ignores transforms, so a target inside
 * an overlay that is still scaling in measures at its resting size. Falls
 * back to bounding rects when the container is not the offset ancestor.
 */
function measure(
    container: HTMLElement | null,
    itemSelector: string,
    activeIndex: number,
): PillRect | null {
    if (container === null) return null;
    const target = container.querySelectorAll(itemSelector)[activeIndex];
    if (!(target instanceof HTMLElement)) return null;

    let x = 0;
    let y = 0;
    let node: HTMLElement | null = target;
    while (node !== null && node !== container) {
        x += node.offsetLeft;
        y += node.offsetTop;
        node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null;
    }
    if (node === container) {
        return { x, y, width: target.offsetWidth, height: target.offsetHeight };
    }

    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    return {
        x: tRect.left - cRect.left,
        y: tRect.top - cRect.top,
        width: tRect.width,
        height: tRect.height,
    };
}

function sameRect(a: PillRect | null, b: PillRect | null): boolean {
    if (a === null || b === null) return a === b;
    return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

const baseStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    zIndex: 0,
    willChange: "transform",
    // The layout effect below places the pill before the first paint.
    opacity: 0,
};

export function SlidingPill({
    containerRef,
    activeIndex,
    itemSelector,
    style,
    className,
}: SlidingPillProps): ReactElement | null {
    const pillRef = useRef<HTMLDivElement>(null);
    const [rect, setRect] = useState<PillRect | null>(null);
    const rafRef = useRef(0);
    const placedRef = useRef(false);
    const reducedMotion = useReducedMotionPreference();

    useLayoutEffect(() => {
        cancelAnimationFrame(rafRef.current);

        if (activeIndex === null || activeIndex < 0) {
            setRect(null);
            return;
        }

        const apply = (): void => {
            const next = measure(containerRef.current, itemSelector, activeIndex);
            setRect((prev) => (sameRect(prev, next) ? prev : next));
        };

        apply();
        rafRef.current = requestAnimationFrame(apply);

        return () => cancelAnimationFrame(rafRef.current);
    }, [activeIndex, containerRef, itemSelector]);

    // The first placement is instant and fades in; later moves morph.
    useLayoutEffect(() => {
        const el = pillRef.current;
        if (el === null || rect === null) {
            placedRef.current = false;
            return;
        }
        const target = {
            transform: thumbTransform(rect.x, rect.y),
            width: px(rect.width),
            height: px(rect.height),
        };
        if (!placedRef.current) {
            placedRef.current = true;
            placeThumb(el, target);
            fadeInThumb(el, reducedMotion ? 0 : motionTokens.scrim.duration);
            return;
        }
        if (reducedMotion) {
            placeThumb(el, target);
            return;
        }
        springThumb(el, target, motionTokens.springs.morph);
    }, [rect, reducedMotion]);

    if (rect === null) return null;

    return <div ref={pillRef} className={className} style={{ ...baseStyle, ...style }} />;
}
